export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Thin fetch wrapper that parses JSON responses, surfaces HTTP errors as
 * ApiError instances (so TanStack Query's onError sees structured info),
 * and sends the auth cookie on every request.
 *
 * Mutation requests (anything other than GET/HEAD/OPTIONS) automatically
 * get an X-Request-Id header — the v1 API's duplicate-request guards
 * (see internal/handler/v1/resource/delete.go) reject requests without
 * one with HTTP 400.
 */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const isMutation =
    method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';

  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(isMutation ? { 'X-Request-Id': crypto.randomUUID() } : {}),
      ...init?.headers,
    },
    ...init,
  });

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      /* response was not JSON; fall through with null body */
    }
    throw new ApiError(
      response.status,
      body,
      `${init?.method ?? 'GET'} ${path} — HTTP ${response.status}`,
    );
  }

  const text = await response.text();
  return (text ? JSON.parse(text) : null) as T;
}
